import React, { useEffect, useRef, useState } from "react";
import styles from "./NestedSelect.module.scss";
import { CategoriesTypes } from "../../../api/types/products.types";
import Arrow from "../../../assets/images/svgs/Arrow";
import Plus from "../../../assets/images/svgs/Plus";
import Minus from "../../../assets/images/svgs/Minus";

interface NestedSelectProps {
  categories: CategoriesTypes[];
  value: number | null;
  onChange: (id: number) => void;
  label?: string;
  className?: string;
  loaderRef?: React.RefObject<HTMLDivElement | null>;
  error?: string;
  language?: string;
}

const NestedSelect: React.FC<NestedSelectProps> = ({
  categories,
  value,
  onChange,
  label,
  className,
  loaderRef,
  error,
  language = "en",
}) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const wrapperRef = useRef<HTMLDivElement>(null);

  const getCategoryName = (category: CategoriesTypes): string => {
    switch (language) {
      case "az":
        return category.name_az || category.name;
      case "en":
        return category.name_en || category.name;
      case "es":
        return category.name_es || category.name;
      case "de":
        return category.name_de || category.name;
      default:
        return category.name;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (id: number) => {
    onChange(id);
    setOpen(false);
  };

  const toggleExpand = (id: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderCategory = (category: CategoriesTypes, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expanded[category.id];

    return (
      <div key={category.id}>
        <div
          className={`${styles.option} ${
            value === category.id ? styles.active : ""
          }`}
          style={{ paddingLeft: level ? `${level * 16}px` : "" }}
        >
          <span
            className={styles.optionText}
            onClick={() => handleSelect(category.id)}
          >
            {getCategoryName(category)}
          </span>

          {hasChildren && (
            <button
              type="button"
              className={styles.expandButton}
              onClick={(e) => toggleExpand(category.id, e)}
            >
              {isExpanded ? <Minus /> : <Plus />}
            </button>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div className={styles.children}>
            {category.children.map((child) => renderCategory(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const selectedCategory = findCategoryById(categories, value);

  return (
    <div>
      <div className={`${styles.wrapper} ${className || ""}`} ref={wrapperRef}>
        {label && <label className={styles.label}>{label}</label>}
        <div
          className={`${styles.selectBox} ${error ? styles.selectError : ""} ${
            open ? styles.selectActive : ""
          }`}
          onClick={() => setOpen((prev) => !prev)}
        >
          {selectedCategory ? getCategoryName(selectedCategory) : "Select..."}
          <Arrow className={open ? styles.arrowUp : styles.arrowDown} />
        </div>

        {open && (
          <div className={styles.dropdown}>
            {categories?.map((cat) => renderCategory(cat)) || (
              <p style={{ padding: 12, textAlign: "center" }}>No categories</p>
            )}
            {loaderRef && (
              <div ref={loaderRef} className={styles.loaderTrigger} />
            )}
          </div>
        )}
      </div>
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
};

function findCategoryById(
  categories: CategoriesTypes[],
  id: number | null,
): CategoriesTypes | null {
  if (id === null) return null;

  for (const cat of categories) {
    if (cat.id === id) return cat;
    if (cat.children) {
      const found = findCategoryById(cat.children, id);
      if (found) return found;
    }
  }
  return null;
}

export default NestedSelect;
